import { useState } from 'react';
import { MapPin, Settings } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTimezoneDetection } from '@/hooks/useTimezoneDetection';
import { usePsychologistContext } from '@/contexts/PsychologistContext';
import { getTimezoneDisplayInfo } from '@/utils/countryTimezones';
import apiCoreAdapter from '@/hooks/apiCoreAdapter';

export function TimezoneChangeNotice() {
  const { browserTz, configuredTz, shouldShow, dismiss, dismissKeep } = useTimezoneDetection();
  const { psychologist, setPsychologist } = usePsychologistContext();
  const [saving, setSaving] = useState(false);
  const [showHowToChange, setShowHowToChange] = useState(false);

  const browserInfo = getTimezoneDisplayInfo(browserTz);
  const configuredInfo = getTimezoneDisplayInfo(configuredTz ?? browserTz);

  const sendMismatchEmail = () => {
    if (!configuredTz) return;
    const emailKey = `psi-tz-mismatch-email-${browserTz}-${configuredTz}`;
    try {
      if (sessionStorage.getItem(emailKey)) return;
      sessionStorage.setItem(emailKey, '1');
    } catch { return; }

    const firstName = psychologist?.name?.split(' ')[0] || 'Psicóloga';
    const html = `<!DOCTYPE html>
<html lang="es">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head>
<body style="margin:0;padding:0;background:#f9fafb;font-family:'Segoe UI',Arial,sans-serif;">
  <div style="max-width:520px;margin:32px auto;background:#fff;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.08);">
    <div style="background-color:#4C1D95;background:linear-gradient(135deg,#4C1D95 0%,#6D28D9 100%);padding:28px 32px;">
      <p style="margin:0;font-size:22px;font-weight:700;color:#fff;">Detectamos una zona horaria diferente ⚠️</p>
      <p style="margin:6px 0 0;font-size:14px;color:#ddd6fe;">Psimammoliti · Psi Platform</p>
    </div>
    <div style="padding:28px 32px;">
      <p style="font-size:15px;color:#374151;margin:0 0 8px;">Hola <strong>${firstName}</strong>, ¿cómo estás?</p>
      <p style="font-size:14px;color:#6b7280;margin:0 0 20px;line-height:1.6;">
        Detectamos que tu dispositivo está usando una zona horaria diferente a la que tenés configurada en tu perfil.
      </p>
      <div style="background:#f5f3ff;border:1px solid #ede9fe;border-radius:12px;padding:16px 20px;margin-bottom:20px;">
        <p style="margin:0 0 12px;font-size:13px;font-weight:600;color:#6D28D9;text-transform:uppercase;letter-spacing:.05em;">Detalle</p>
        <div style="display:flex;flex-direction:column;gap:12px;">
          <div>
            <p style="margin:0;font-size:11px;color:#9ca3af;text-transform:uppercase;letter-spacing:.04em;">Tu dispositivo</p>
            <p style="margin:4px 0 0;font-size:14px;font-weight:600;color:#374151;">${browserInfo.label}</p>
            <p style="margin:2px 0 0;font-size:12px;color:#9ca3af;">${browserInfo.offset}</p>
          </div>
          <div>
            <p style="margin:0;font-size:11px;color:#9ca3af;text-transform:uppercase;letter-spacing:.04em;">Tu perfil configurado</p>
            <p style="margin:4px 0 0;font-size:14px;font-weight:600;color:#6D28D9;">${configuredInfo.label}</p>
            <p style="margin:2px 0 0;font-size:12px;color:#9ca3af;">${configuredInfo.offset}</p>
          </div>
        </div>
      </div>
      <div style="background:#fefce8;border:1px solid #fef08a;border-radius:12px;padding:16px 20px;margin-bottom:20px;">
        <p style="margin:0 0 8px;font-size:13px;font-weight:600;color:#854d0e;">¿Qué implica esto?</p>
        <p style="margin:0;font-size:13px;color:#713f12;line-height:1.6;">
          Los horarios de tus sesiones en la plataforma seguirán mostrándose según tu zona horaria configurada (<strong>${configuredInfo.label}</strong>). Si te encontrás en otra ubicación, los horarios podrían no coincidir con tu reloj local.
        </p>
      </div>
      <div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:12px;padding:16px 20px;margin-bottom:20px;">
        <p style="margin:0 0 8px;font-size:13px;font-weight:600;color:#166534;">¿Cómo actualizar tu zona horaria?</p>
        <p style="margin:0;font-size:13px;color:#14532d;line-height:1.6;">
          Podés actualizar tu zona horaria en cualquier momento desde tu <strong>Perfil</strong>, en la sección de configuración general. El cambio se aplicará de inmediato.
        </p>
      </div>
      <p style="font-size:12px;color:#9ca3af;margin:0;">Elegiste mantener tu zona horaria configurada (<strong>${configuredInfo.label}</strong>) sin actualizarla. Si en algún momento cambiás de opinión, podés hacerlo desde tu Perfil.</p>
    </div>
    <div style="background:#f3f4f6;padding:16px 32px;text-align:center;">
      <p style="margin:0;font-size:12px;color:#9ca3af;">¿Dudas? Escribinos a <a href="mailto:psicos@psimammoliti.com" style="color:#6D28D9;">psicos@psimammoliti.com</a></p>
    </div>
  </div>
</body>
</html>`;
    void apiCoreAdapter.psychologistNotifyProfileChanges({
      html,
      subject: 'Detectamos una zona horaria diferente a la configurada',
    });
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      await apiCoreAdapter.updatePsychologistProfile({ timezone: browserTz });
      if (psychologist) {
        setPsychologist({ ...psychologist, timezone: browserTz });
      }
      try { localStorage.setItem('psi-timezone', browserTz); } catch { /* noop */ }
    } catch { /* noop — el error ya lo maneja secureHttp con toast */ }
    setSaving(false);
    dismiss();
  };

  if (!shouldShow) return null;

  if (showHowToChange) {
    return (
      <Dialog open onOpenChange={(open) => { if (!open) { sendMismatchEmail(); dismissKeep(); } }}>
        <DialogContent style={{ maxWidth: '400px', padding: '1.5rem' }} onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings size={18} className="text-violet-600" />
              ¿Cómo cambio mi zona horaria?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600 leading-relaxed">
              Podés actualizar tu zona horaria en cualquier momento desde tu <span className="font-semibold text-gray-800">Perfil</span>, en la sección de configuración general.
            </p>
            <div className="flex flex-col gap-2 pt-1">
              <Button onClick={() => { sendMismatchEmail(); dismissKeep(); }} className="w-full">
                Entendido
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open) dismiss(); }}>
      <DialogContent style={{ maxWidth: '400px', padding: '1.5rem' }} onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin size={18} className="text-violet-600" />
            ¿Cambiaste de ubicación?
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex items-center gap-1.5 overflow-hidden whitespace-nowrap" title={`Tu dispositivo: ${browserInfo.label} (${browserInfo.offset})`}>
              <span className="shrink-0">Tu dispositivo:</span>
              <span className="font-semibold text-gray-800 truncate min-w-0">{browserInfo.label}</span>
              <span className="text-gray-400 shrink-0">({browserInfo.offset})</span>
            </div>
            <div className="flex items-center gap-1.5 overflow-hidden whitespace-nowrap" title={`Tu perfil: ${configuredInfo.label} (${configuredInfo.offset})`}>
              <span className="shrink-0">Tu perfil:</span>
              <span className="font-semibold text-gray-800 truncate min-w-0">{configuredInfo.label}</span>
              <span className="text-gray-400 shrink-0">({configuredInfo.offset})</span>
            </div>
          </div>
          <p className="text-sm text-gray-500">¿Querés actualizar tu zona horaria?</p>
          <div className="flex flex-col gap-2 pt-1">
            <Button onClick={handleUpdate} disabled={saving} className="w-full">
              {saving ? 'Actualizando…' : `Actualizar a ${browserInfo.label}`}
            </Button>
            <Button variant="ghost" onClick={() => setShowHowToChange(true)} disabled={saving} className="w-full bg-gray-100">
              Mantener {configuredInfo.label}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
